import type { ServerMessage } from 'ts-components/PiChat/protocol';
import type { PiChatFrameMeta } from 'ts-components/PiChat/usePiChat';
import {
  resolveCaptureUpload,
  sendCaptureOutcome,
  type CaptureOutcome,
} from './captureUpload';

export type CaptureMessage = Extract<
  ServerMessage,
  { type: 'block.capture' | 'ui.capture' }
>;

export interface CaptureContext {
  signal: AbortSignal;
  reply: (outcome: CaptureOutcome) => void;
}

export const handleCaptureRequest = <M extends CaptureMessage>(
  message: M,
  { url, signal, isCurrent }: PiChatFrameMeta,
  capture: (message: M, context: CaptureContext) => void,
  inFlight: WeakSet<AbortSignal>,
  send = sendCaptureOutcome
) => {
  const upload = resolveCaptureUpload(message.upload, url, message.type);
  if (!upload) return;
  if (inFlight.has(signal)) {
    send(upload, {
      ok: false,
      code: 'capture_failed',
      message: 'Another capture is still in progress',
    });
    return;
  }
  inFlight.add(signal);
  const controller = new AbortController();
  const captureSignal = controller.signal;
  const abort = () => controller.abort();
  const timer = setTimeout(abort, upload.expiresAt - Date.now());
  let held = true;
  const release = () => {
    if (!held) return;
    held = false;
    inFlight.delete(signal);
    clearTimeout(timer);
    signal.removeEventListener('abort', abort);
  };
  captureSignal.addEventListener('abort', release);
  signal.addEventListener('abort', abort);
  let replied = false;
  const reply = (outcome: CaptureOutcome) => {
    if (replied || captureSignal.aborted) return;
    replied = true;
    if (!isCurrent()) {
      release();
      return;
    }
    void send(upload, outcome).finally(release);
  };
  try {
    capture(message, { signal: captureSignal, reply });
  } catch (error) {
    reply({
      ok: false,
      code: 'capture_failed',
      message: error instanceof Error ? error.message : String(error),
    });
  }
};
